import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Layout } from '@/ui/Layout';
import { Button } from '@/ui/Button';
import { TitleStyled } from '@/administration/pages/Journey/pages/ViewDiscussions/styles';
import { ViewTeamsContent } from '@/administration/pages/Journey/pages/ViewTeams/ViewTeamsContent';
import {
  RLearningJourneySessionDiscussionTeam,
  RUpsertLearningJourneySessionDiscussionTeamMembersInput,
  useUpsertLearningJourneySessionDiscussionTeamMembersMutationRemote,
} from '@/store/v2';
import { SharedSnackbarContext, SnackbarType } from '@/component/SharedSnackbar/SharedSnackbar';

const TitleSectionStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 40px;
`;

const TitleContainerStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ActionsStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ViewTeams = (): React.ReactElement => {
  const navigate = useNavigate();
  const { openSnackbar } = useContext(SharedSnackbarContext);
  // In reset mode we allow user to reselect teams size and create teams one more time from scratch
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [teams, setTeams] = useState<RLearningJourneySessionDiscussionTeam[]>([]);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [upsertTeamMember] = useUpsertLearningJourneySessionDiscussionTeamMembersMutationRemote();

  const handleNavigateBack = () => {
    navigate(-1);
  };

  const handleSaveReordering = async () => {
    const teamMembers: RUpsertLearningJourneySessionDiscussionTeamMembersInput[] = [];

    teams.forEach((team) => {
      team?.members?.forEach((member) => {
        teamMembers.push({ teamId: team?.id as number, userId: member?.user?.id });
      });
    });

    const { data } = await upsertTeamMember({ variables: { sessionId: sessionId as number, teamMembers } });

    if (data?.upsertLearningJourneySessionDiscussionTeamMembers?.success) {
      openSnackbar({
        message: 'Learning teams updated',
        isDismissive: true,
      });
    } else {
      openSnackbar({
        message: 'Something went wrong',
        type: SnackbarType.DANGER,
        isDismissive: true,
      });
    }
  };

  const handleResetClick = () => {
    setIsResetMode(true);
  };

  return (
    <>
      <Layout.ContentSection marginTop>
        <Button variant="ghost" icon="arrow-left" onClick={handleNavigateBack}>
          Back
        </Button>
        <TitleSectionStyled>
          <TitleContainerStyled>
            <TitleStyled>Learning teams</TitleStyled>
          </TitleContainerStyled>
          {(teams?.length > 0 || isResetMode) && (
            <ActionsStyled>
              <Button variant="secondary" onClick={handleResetClick}>
                Reset
              </Button>
              <Button variant="primary" onClick={handleSaveReordering} disabled={isSaveDisabled}>
                Save
              </Button>
            </ActionsStyled>
          )}
        </TitleSectionStyled>
      </Layout.ContentSection>
      <Layout.ContentSection marginTop marginBottom>
        <ViewTeamsContent
          teams={teams}
          setTeams={setTeams}
          setSessionId={setSessionId}
          isResetMode={isResetMode}
          setIsResetMode={setIsResetMode}
          setIsSaveDisabled={setIsSaveDisabled}
        />
      </Layout.ContentSection>
    </>
  );
};
